import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  Tab,
  Tabs,
  Typography,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  SmartToy as AssistantIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/DashboardLayout';
import AssistantCard from '../../components/AssistantCard';
import * as assistantService from '../../services/assistantService';
import * as integrationService from '../../services/integrationService';
import { useAuth } from '../../hooks/useAuth';

// Модели и таймзоны
const availableModels = [
  'gpt-4o-mini',
  'gpt-o4-mini',
  'gpt-4.1-mini',
  'gpt-5-mini',
  'gpt-4o',
  'gpt-4.1',
  'gpt-5',
];
const timezones = [
  'UTC', 'UTC+2', 'UTC+3', 'UTC+4', 'UTC+5', 'UTC+6',
  'UTC+7', 'UTC+8', 'UTC+9', 'UTC+10', 'UTC+11', 'UTC+12'
];

const AssistantsPage = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [assistants, setAssistants] = useState([]);
  const [allInstances, setAllInstances] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [dialogMode, setDialogMode] = useState('create');
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [loadingDialog, setLoadingDialog] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false, message: '', severity: 'success'
  });

  // Для выбора инстансов и интеграций
  const [selectedInstances, setSelectedInstances] = useState([]);
  const [originalInstances, setOriginalInstances] = useState([]);
  const [selectedIntegrations, setSelectedIntegrations] = useState([]);
  const [originalIntegrations, setOriginalIntegrations] = useState([]);
  const [availableIntegrations, setAvailableIntegrations] = useState([]);

  // Форма
  const [formData, setFormData] = useState({
    name: '',
    model: 'gpt-4o-mini',
    temperature: 0.7,
    prompt: 'Ты ИИ помощник, умеющий вести диалоги на общие темы.',
    timezone: user?.timezone || 'UTC+3',
    response_delay: 0
  });

  // ==============================
  // 1. Получаем всю страницу ОДНИМ запросом
  // ==============================
  const fetchPageData = useCallback(async () => {
    setLoadingPage(true);
    setError(null);
    try {
      const data = await assistantService.getAssistantsPageData();
      // Данные с бэка: { assistants: [...], allInstances: [...] }
      setAssistants(data.assistants || []);
      setAllInstances(data.allInstances || []);
      
      // Fetch available integrations
      const integrations = await integrationService.getUserIntegrations();
      setAvailableIntegrations(integrations || []);
    } catch (err) {
      setError('Failed to load assistants data');
      setSnackbar({
        open: true, message: 'Failed to load assistants data', severity: 'error'
      });
    } finally {
      setLoadingPage(false);
    }
  }, []);

  // ==============================
  // 2. Мемо-фильтр ассистентов по табам
  // ==============================
  const filteredAssistants = useMemo(() => {
    if (tabValue === 1) return assistants.filter(a => a.status === 'active');
    if (tabValue === 2) return assistants.filter(a => a.status === 'inactive');
    return assistants;
  }, [assistants, tabValue]);

  // ==============================
  // 3. Получение списка выбранных инстансов для текущего ассистента (из данных страницы)
  // ==============================
  const getInstancesForAssistant = useCallback((assistantId) => {
    const assistant = assistants.find(a => a.id === assistantId);
    return assistant && Array.isArray(assistant.Instances)
        ? assistant.Instances
        : [];
  }, [assistants]);

  // ==============================
  // 4. Список доступных инстансов (для селекта)
  // ==============================
  const getAvailableInstances = useCallback(() => {
    // Инстанс может быть выбран только в одном ассистенте, кроме текущего редактируемого ассистента
    // Для create — показываем все свободные, для edit — те, что были изначально + свободные
    // Флаг isAssigned формируем по данным allInstances
    let assignedInstanceIds = [];
    assistants.forEach(a => {
      a.Instances.forEach(inst => {
        if (!selectedAssistant || a.id !== selectedAssistant.id) {
          assignedInstanceIds.push(inst.id);
        }
      });
    });
    return allInstances.map(inst => {
      const isAssigned = assignedInstanceIds.includes(inst.id);
      // В режиме edit разрешаем выбирать те, которые были привязаны к ассистенту до этого
      const isOriginallyAssigned = originalInstances.some(oi => oi.id === inst.id);
      return {
        ...inst,
        isAssigned: isAssigned && !isOriginallyAssigned
      };
    });
  }, [allInstances, assistants, selectedAssistant, originalInstances]);

  // ==============================
  // 5. useEffect — загрузка данных
  // ==============================
  useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  // ==============================
  // 6. Открытие формы "создать"
  // ==============================
  const handleDialogOpen = useCallback(() => {
    setDialogMode('create');
    setSelectedAssistant(null);
    setSelectedInstances([]);
    setOriginalInstances([]);
    setSelectedIntegrations([]);
    setOriginalIntegrations([]);
    setIsViewOnly(false);
    setFormData({
      name: '',
      model: 'gpt-4o-mini',
      temperature: 0.7,
      prompt: 'Ты ИИ помощник, умеющий вести диалоги на общие темы.',
      timezone: user?.timezone || 'UTC+3',
      response_delay: 0
    });
    setOpenDialog(true);
  }, [user]);

  // ==============================
  // 7. Открытие формы "редактировать"
  // ==============================
  const handleEditDialogOpen = useCallback(async (assistant) => {
    if (!assistant) return;
    setDialogMode('edit');
    setIsViewOnly(assistant.status === 'active');
    setSelectedAssistant(assistant);
    setFormData({
      name: assistant.name || '',
      model: assistant.model || '',
      temperature: assistant.temperature || 0.7,
      prompt: assistant.prompt || '',
      timezone: assistant.timezone || user?.timezone || 'UTC+3',
      response_delay: assistant.response_delay || 0
    });
    
    // Выставляем выбранные и оригинальные инстансы по данным ассистента
    const instances = getInstancesForAssistant(assistant.id);
    setSelectedInstances(instances);
    setOriginalInstances(instances);
    
    // Получаем интеграции ассистента
    try {
      const integrations = await assistantService.getAssistantIntegrations(assistant.id);
      setSelectedIntegrations(integrations);
      setOriginalIntegrations(integrations);
    } catch (error) {
      console.error('Error fetching assistant integrations:', error);
      setSelectedIntegrations([]);
      setOriginalIntegrations([]);
    }
    
    setOpenDialog(true);
  }, [getInstancesForAssistant, user]);

  // ==============================
  // 8. Прочие обработчики формы
  // ==============================
  const handleDialogClose = useCallback(() => {
    setOpenDialog(false);
    setLoadingDialog(false);
  }, []);
  const handleTabChange = (e, val) => setTabValue(val);
  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'temperature' ? parseFloat(value) : value }));
  }, []);
  const handleMultiSelectChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);
  const handleInstanceSelectionChange = useCallback((e) => {
    // Получаем объекты по id
    const selectedIds = e.target.value;
    const available = getAvailableInstances();
    setSelectedInstances(
      selectedIds.map((id) => {
        const found = available.find((inst) => inst.id === id);
        return found || { id, alias: String(id), isAssigned: false };
      })
    );
  }, [getAvailableInstances]);
  
  const handleIntegrationSelectionChange = useCallback((e) => {
    // Получаем объекты по id
    const selectedIds = e.target.value;
    setSelectedIntegrations(
      selectedIds.map((id) => {
        const found = availableIntegrations.find((integration) => integration.id === id);
        return found || { id, name: String(id) };
      })
    );
  }, [availableIntegrations]);

  // ==============================
  // 9. CRUD ассистентов
  // ==============================
  const handleCreateAssistant = useCallback(async () => {
    setLoadingAction(true);
    try {
      const assistantData = { ...formData, model: 'gpt-4o-mini' };
      const createdAssistant = await assistantService.createAssistant(assistantData);
      
      // Add instances if selected
      if (selectedInstances.length > 0) {
        await assistantService.addInstancesToAssistant(
            createdAssistant.id,
            selectedInstances.map(i => i.id)
        );
      }
      
      // Add integrations if selected
      if (selectedIntegrations.length > 0) {
        await assistantService.addIntegrationsToAssistant(
            createdAssistant.id,
            selectedIntegrations.map(i => i.id)
        );
      }
      
      setSnackbar({ open: true, message: 'Assistant created successfully', severity: 'success' });
      await fetchPageData();
      handleDialogClose();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to create assistant', severity: 'error' });
    } finally {
      setLoadingAction(false);
    }
  }, [formData, selectedInstances, selectedIntegrations, fetchPageData, handleDialogClose]);

  const handleUpdateAssistant = useCallback(async () => {
    if (selectedAssistant && selectedAssistant.status === 'active') {
      setSnackbar({
        open: true, message: 'Cannot update assistant while it is running. Please stop it first.', severity: 'warning'
      });
      handleDialogClose();
      return;
    }
    setLoadingAction(true);
    try {
      await assistantService.updateAssistant(selectedAssistant.id, formData);
      
      // Новые/удалённые инстансы
      const selectedInstanceIds = selectedInstances.map(i => i.id);
      const originalInstanceIds = originalInstances.map(i => i.id);
      const instancesToAdd = selectedInstanceIds.filter(id => !originalInstanceIds.includes(id));
      const instancesToRemove = originalInstanceIds.filter(id => !selectedInstanceIds.includes(id));
      
      if (instancesToAdd.length > 0) {
        await assistantService.addInstancesToAssistant(selectedAssistant.id, instancesToAdd);
      }
      if (instancesToRemove.length > 0) {
        await assistantService.removeInstancesFromAssistant(selectedAssistant.id, instancesToRemove);
      }
      
      // Новые/удалённые интеграции
      const selectedIntegrationIds = selectedIntegrations.map(i => i.id);
      const originalIntegrationIds = originalIntegrations.map(i => i.id);
      const integrationsToAdd = selectedIntegrationIds.filter(id => !originalIntegrationIds.includes(id));
      const integrationsToRemove = originalIntegrationIds.filter(id => !selectedIntegrationIds.includes(id));
      
      if (integrationsToAdd.length > 0) {
        await assistantService.addIntegrationsToAssistant(selectedAssistant.id, integrationsToAdd);
      }
      if (integrationsToRemove.length > 0) {
        await assistantService.removeIntegrationsFromAssistant(selectedAssistant.id, integrationsToRemove);
      }
      
      setSnackbar({ open: true, message: 'Assistant updated successfully', severity: 'success' });
      await fetchPageData();
      handleDialogClose();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update assistant', severity: 'error' });
    } finally {
      setLoadingAction(false);
    }
  }, [selectedAssistant, formData, selectedInstances, originalInstances, 
      selectedIntegrations, originalIntegrations, fetchPageData, handleDialogClose]);

  // Удаление ассистента
  const handleDeleteDialogOpen = useCallback((assistant) => {
    if (assistant.status === 'active') {
      setSnackbar({
        open: true,
        message: 'Cannot delete assistant while it is running. Please stop it first.',
        severity: 'warning'
      });
      return;
    }
    setSelectedAssistant(assistant);
    setOpenDeleteDialog(true);
  }, []);
  const handleDeleteDialogClose = () => setOpenDeleteDialog(false);
  const handleDeleteAssistant = useCallback(async () => {
    if (selectedAssistant && selectedAssistant.status === 'active') {
      setSnackbar({
        open: true,
        message: 'Cannot delete assistant while it is running. Please stop it first.',
        severity: 'warning'
      });
      handleDeleteDialogClose();
      return;
    }
    setLoadingAction(true);
    try {
      await assistantService.deleteAssistant(selectedAssistant.id);
      setSnackbar({ open: true, message: 'Assistant deleted successfully', severity: 'success' });
      await fetchPageData();
      handleDeleteDialogClose();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete assistant', severity: 'error' });
    } finally {
      setLoadingAction(false);
    }
  }, [selectedAssistant, fetchPageData]);

  // Запуск/остановка ассистента (оставляем как есть)
  const handleStartAssistant = useCallback(async (assistant) => {
    setLoadingAction(true);
    try {
      await assistantService.startAssistant(assistant.id);
      setSnackbar({ open: true, message: 'Assistant started successfully', severity: 'success' });
      await fetchPageData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to start assistant', severity: 'error' });
    } finally {
      setLoadingAction(false);
    }
  }, [fetchPageData]);
  const handleStopAssistant = useCallback(async (assistant) => {
    setLoadingAction(true);
    try {
      await assistantService.stopAssistant(assistant.id);
      setSnackbar({ open: true, message: 'Assistant stopped successfully', severity: 'success' });
      await fetchPageData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to stop assistant', severity: 'error' });
    } finally {
      setLoadingAction(false);
    }
  }, [fetchPageData]);
  const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));

  // ==============================
  // JSX
  // ==============================
  return (
      <DashboardLayout>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            AI Assistants
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your AI assistants for automated conversations
          </Typography>
        </Box>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="assistant tabs">
            <Tab label="All Assistants" />
            <Tab label="Active" />
            <Tab label="Inactive" />
          </Tabs>
          <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleDialogOpen}
              disabled={loadingPage}
          >
            New Assistant
          </Button>
        </Box>
        {loadingPage && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}
        {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
        )}
        {!loadingPage && !error && filteredAssistants.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <AssistantIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No assistants found
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Create your first AI assistant to automate conversations
              </Typography>
              <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleDialogOpen}
              >
                Create Assistant
              </Button>
            </Paper>
        )}
        <Grid container spacing={3}>
          {filteredAssistants.map((assistant) => (
              <Grid item xs={6} md={4} lg={3} key={assistant.id}>
                <AssistantCard
                    assistant={assistant}
                    onEdit={handleEditDialogOpen}
                    onDelete={handleDeleteDialogOpen}
                    onStart={handleStartAssistant}
                    onStop={handleStopAssistant}
                />
              </Grid>
          ))}
        </Grid>
        {/* Диалог создания/редактирования ассистента */}
        <Dialog
            open={openDialog}
            onClose={handleDialogClose}
            maxWidth="md"
            fullWidth
        >
          <DialogTitle>
            {dialogMode === 'create'
                ? 'Create New Assistant'
                : isViewOnly
                    ? 'View Assistant (Running - Cannot Edit)'
                    : 'Edit Assistant'
            }
          </DialogTitle>
          <DialogContent>
            {loadingDialog ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3} sx={{ mt: 0 }}>
                  <Grid item xs={12}>
                    <TextField
                        fullWidth label="Name" name="name"
                        value={formData.name} onChange={handleFormChange}
                        margin="normal" required
                        InputProps={{ readOnly: isViewOnly }}
                        helperText={isViewOnly ? "Cannot edit while assistant is running" : ""}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth label="Temperature" name="temperature"
                        type="number" inputProps={{ min: 0, max: 1, step: 0.1 }}
                        value={formData.temperature} onChange={handleFormChange}
                        margin="normal"
                        helperText={isViewOnly ? "Cannot edit while assistant is running" : "Value between 0 and 1"}
                        InputProps={{ readOnly: isViewOnly }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth label="Response Delay (seconds)" name="response_delay"
                        type="number" inputProps={{ min: 0, step: 1 }}
                        value={formData.response_delay} onChange={handleFormChange}
                        margin="normal"
                        helperText={isViewOnly ? "Cannot edit while assistant is running" : "Delay before responds (0 = no delay)"}
                        InputProps={{ readOnly: isViewOnly }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel id="timezone-label">Часовой пояс (UTC смещение)</InputLabel>
                      <Select
                          labelId="timezone-label"
                          id="timezone"
                          name="timezone"
                          value={formData.timezone}
                          label="Часовой пояс (UTC смещение)"
                          onChange={handleFormChange}
                          disabled={isViewOnly}
                      >
                        {timezones.map((tz) => (
                            <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                        ))}
                      </Select>
                      {isViewOnly && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            Cannot edit while assistant is running
                          </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                        fullWidth label="Prompt" name="prompt"
                        value={formData.prompt} onChange={handleFormChange}
                        multiline rows={4} margin="normal"
                        InputProps={{ readOnly: isViewOnly }}
                        helperText={isViewOnly ? "Cannot edit while assistant is running" : ""}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel id="integrations-label">Integrations</InputLabel>
                      <Select
                          labelId="integrations-label"
                          multiple
                          value={selectedIntegrations.map(integration => integration.id)}
                          onChange={handleIntegrationSelectionChange}
                          label="Integrations"
                          disabled={isViewOnly}
                          renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((id) => {
                                  const integration = availableIntegrations.find(i => i.id === id);
                                  return (
                                      <Chip 
                                        key={id} 
                                        label={integration ? integration.name : String(id ?? '')}
                                        avatar={integration && integration.Tool && integration.Tool.icon ? 
                                          <Avatar src={integration.Tool.icon} alt={integration.Tool.name} /> : 
                                          undefined
                                        }
                                      />
                                  );
                                })}
                              </Box>
                          )}
                      >
                        {availableIntegrations.map((integration) => (
                            <MenuItem
                                key={integration.id}
                                value={integration.id}
                                sx={{
                                  position: 'relative'
                                }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {integration.Tool && integration.Tool.icon ? (
                                  <Avatar 
                                    src={integration.Tool.icon} 
                                    alt={integration.Tool.name} 
                                    sx={{ width: 24, height: 24, mr: 1 }} 
                                  />
                                ) : null}
                                {integration.name}
                                {integration.Tool ? (
                                  <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                    ({integration.Tool.name})
                                  </Typography>
                                ) : null}
                              </Box>
                            </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Select integrations to use with this assistant
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel id="instances-label">Instances</InputLabel>
                      <Select
                          labelId="instances-label"
                          multiple
                          value={selectedInstances.map(inst => inst.id)}
                          onChange={handleInstanceSelectionChange}
                          label="Instances"
                          disabled={isViewOnly}
                          renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((id) => {
                                  const inst = allInstances.find(i => i.id === id);
                                  return (
                                      <Chip key={id} label={inst && inst.alias ? inst.alias : String(id ?? '')} />
                                  );
                                })}
                              </Box>
                          )}
                      >
                        {getAvailableInstances().map((inst) => (
                            <MenuItem
                                key={inst.id}
                                value={inst.id}
                                disabled={inst.isAssigned}
                                sx={{
                                  opacity: !inst.isAssigned ? 1 : 0.5,
                                  position: 'relative'
                                }}
                                title={inst.isAssigned ? "This instance is already assigned to another assistant" : ""}
                            >
                              {inst.alias}
                            </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Note: Instances can only be assigned to one assistant at a time
                      </Typography>
                    </FormControl>
                  </Grid>
                </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>{isViewOnly ? 'Close' : 'Cancel'}</Button>
            {!isViewOnly && (
                <Button
                    onClick={dialogMode === 'create' ? handleCreateAssistant : handleUpdateAssistant}
                    variant="contained"
                    disabled={loadingAction || !formData.name}
                >
                  {loadingAction ? <CircularProgress size={24} /> : (dialogMode === 'create' ? 'Create' : 'Update')}
                </Button>
            )}
          </DialogActions>
        </Dialog>
        {/* Диалог подтверждения удаления */}
        <Dialog
            open={openDeleteDialog}
            onClose={handleDeleteDialogClose}
            maxWidth="sm"
            fullWidth
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the assistant "{selectedAssistant?.name || 'Unknown'}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteDialogClose}>Cancel</Button>
            <Button
                onClick={handleDeleteAssistant}
                variant="contained"
                color="error"
                disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Snackbar для уведомлений */}
        <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </DashboardLayout>
  );
};

export default AssistantsPage;
